import { useState, useEffect } from 'react'
import { usersApi } from '../../api/users'
import type { PrivacySettings } from '../../api/users'
import { useUiStore } from '../../stores/uiStore'
import { Spinner } from '../ui/Spinner'
import { Select } from '../ui/Select'
import { TwoFactorSetup } from './TwoFactorSetup'

const OPTIONS = ['everyone', 'contacts', 'nobody'] as const
type Option = typeof OPTIONS[number]

const LABELS: Record<Option, string> = {
  everyone: 'Все',
  contacts: 'Контакты',
  nobody: 'Никто',
}

interface FieldProps {
  label: string
  value: string
  options?: readonly string[]
  onChange: (v: string) => void
}

function PrivacyField({ label, value, options = OPTIONS, onChange }: FieldProps) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-txt-primary text-sm">{label}</span>
      <Select
        value={value}
        onChange={onChange}
        options={options.map(o => ({ value: o, label: LABELS[o as Option] ?? o }))}
      />
    </div>
  )
}

interface Props { onBack: () => void }

export function PrivacySettings({ onBack }: Props) {
  const { addToast } = useUiStore()
  const [settings, setSettings] = useState<PrivacySettings | null>(null)
  const [saving, setSaving] = useState(false)
  const [show2FA, setShow2FA] = useState(false)

  if (show2FA) return <TwoFactorSetup onBack={() => setShow2FA(false)} />

  useEffect(() => {
    usersApi.getPrivacy()
      .then(s => setSettings(s))
      .catch(() => addToast('error', 'Не удалось загрузить настройки приватности'))
  }, [addToast])

  const setField = <K extends keyof PrivacySettings>(k: K, v: PrivacySettings[K]) => {
    setSettings(s => s ? { ...s, [k]: v } : s)
  }

  const handleSave = async () => {
    if (!settings) return
    setSaving(true)
    try {
      const updated = await usersApi.updatePrivacy(settings)
      setSettings(updated as PrivacySettings)
      addToast('success', 'Настройки приватности сохранены')
    } catch {
      addToast('error', 'Не удалось сохранить настройки')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col h-full bg-bg-primary">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-divider shrink-0">
        <button onClick={onBack} className="text-txt-secondary hover:text-txt-primary transition-colors">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </button>
        <h2 className="text-txt-primary font-semibold text-base flex-1">Приватность</h2>
        <button onClick={handleSave} disabled={saving || !settings}
          className="text-accent text-sm font-medium disabled:opacity-40 hover:opacity-80 transition-opacity">
          {saving ? '...' : 'Сохранить'}
        </button>
      </div>

      {!settings ? (
        <div className="flex-1 flex items-center justify-center"><Spinner /></div>
      ) : (
        <div className="flex-1 overflow-y-auto p-4 space-y-1">
          <p className="text-txt-secondary text-xs uppercase tracking-wider font-medium mb-3">Кто может видеть</p>
          <PrivacyField label="Номер телефона" value={settings.phone_visibility}
            onChange={v => setField('phone_visibility', v)} />
          <PrivacyField label="Фото профиля" value={settings.avatar_visibility}
            onChange={v => setField('avatar_visibility', v)} />
          <PrivacyField label="Последний вход" value={settings.last_seen_visibility}
            onChange={v => setField('last_seen_visibility', v)} />

          <div className="border-t border-divider mt-4 pt-4">
            <p className="text-txt-secondary text-xs uppercase tracking-wider font-medium mb-3">Кто может</p>
            <PrivacyField label="Добавлять в группы" value={settings.who_can_add_to_groups}
              onChange={v => setField('who_can_add_to_groups', v)} />
            <PrivacyField label="Писать сообщения" value={settings.who_can_message}
              onChange={v => setField('who_can_message', v)} />
          </div>

          {/* Two-Factor Authentication */}
          <div className="border-t border-divider mt-4 pt-4">
            <p className="text-txt-secondary text-xs uppercase tracking-wider font-medium mb-3">Безопасность</p>
            <button
              onClick={() => setShow2FA(true)}
              className="w-full flex items-center justify-between py-2.5 px-3 rounded-xl bg-bg-input hover:bg-bg-hover transition-colors"
            >
              <span className="flex items-center gap-2 text-txt-primary text-sm">
                🔐 Двухфакторная аутентификация
              </span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-txt-meta">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
          </div>

        </div>
      )}
    </div>
  )
}
